import { useCallback, useEffect, useState } from 'react';
import type { NocStationDetail } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { nocApi } from '@/features/noc/api/nocApi';

const POLL_MS = 3000;

/** Detalle de estación con refresco periódico (conectores + métricas en vivo). */
export function useStationLive(stationId: string | null): {
  detail: NocStationDetail | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [detail, setDetail] = useState<NocStationDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!stationId) {
      setDetail(null);
      setError(null);
      return;
    }
    let active = true;
    setLoading(true);
    const load = async () => {
      try {
        const response = await nocApi.station(stationId);
        if (active) {
          setDetail(response);
          setError(null);
        }
      } catch (caught) {
        if (active) {
          setError(
            caught instanceof ApiError ? caught.message : 'No se pudo leer la estación.',
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void load();
    const intervalId = window.setInterval(load, POLL_MS);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [stationId, refreshKey]);

  const refresh = useCallback(() => setRefreshKey((key) => key + 1), []);
  return { detail, loading, error, refresh };
}
